import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  FaFlask,
  FaUniversity,
  FaTruck,
  FaLandmark,
  FaLink,
  FaGlobe
} from 'react-icons/fa';

import biocollate from '../assets/biocollate.png';
import cubouldercs from '../assets/cubouldercs.jpeg';
import acs from '../assets/acs.jpeg';
import tiaa from '../assets/tiaa.jpeg';
import richedu from '../assets/richedu.jpeg';
import jms from '../assets/jms.jpeg';

/* ── Main timeline entries — descending by start date ── */
const experiences = [
  {
    id: 1,
    company: 'BioCollate',
    role: 'Capstone Project',
    period: 'Sep 2025 – May 2026',
    location: 'Boulder, CO · Hybrid',
    image: biocollate,
    description:
      'Designing a secure P2P networking protocol for encrypted and verifiable data exchange among synthetic biology researchers.',
    achievements: [
      'Designed secure P2P protocol with E2E encryption and dynamic routing',
      'Implemented Diffie–Hellman key exchange and hashing for integrity',
      'Integrated distributed ledger for tamper-proof traceability',
      'Built cross-platform desktop app using Dart, Python, and Flutter',
      'Collaborated with sponsors and faculty on security audits',
    ],
    technologies: ['Dart', 'Python', 'Flutter', 'Cryptography', 'P2P', 'DLT'],
    icon: <FaFlask />,
  },
  {
    id: 3,
    company: 'Ascend Cargo Systems',
    role: 'Full Stack Engineer (Intern)',
    period: 'Jun 2025 – Dec 2025',
    location: 'Remote · USA',
    image: acs,
    description:
      'Backend-focused internship on production logistics systems used by real freight clients.',
    achievements: [
      'Automated 35+ workflows including carrier assignment and shipment tendering, reducing the process from hours to seconds',
      'Built an SFTP-based ingestion pipeline that parsed incoming EDI 850 files and auto-created shipments, eliminating manual file transfers',
      'Designed distributed locking with Spring Task Scheduler to prevent conflicting updates across a multi-server DigitalOcean deployment',
      'Helped onboard the company\'s first production client',
    ],
    technologies: ['Java', 'Spring Boot', 'MySQL', 'Spring Task Scheduler', 'SFTP', 'EDI 850', 'DigitalOcean'],
    icon: <FaTruck />,
  },
  {
    id: 2,
    company: 'University of Colorado Boulder',
    role: 'Grader – CSCI 4448/5448',
    period: 'Aug 2024 – May 2026',
    location: 'Boulder, CO',
    image: cubouldercs,
    description: 'Grader for Object Oriented Analysis and Design.',
    achievements: [
      'Evaluated coursework and provided technical feedback on design pattern implementation and OOP principles for undergraduate and graduate students',
    ],
    technologies: ['Java', 'OOP', 'Design Patterns'],
    icon: <FaUniversity />,
  },
  {
    id: 4,
    company: 'TIAA',
    role: 'Software Developer Analyst',
    period: 'Jul 2022 – Jul 2024',
    location: 'Mumbai, India',
    image: tiaa,
    description: 'Backend engineer at a US-based financial services organization.',
    achievements: [
      'Designed and owned distributed microservices for a financial system processing 50K+ daily transactions using Spring Boot and Kafka-based async pipelines',
      'Reduced API response time from 3–5 minutes to under a minute by migrating legacy data to MongoDB and converting synchronous calls to async (ExecutorService, CompletableFuture); built a custom rules engine for business routing logic',
      'Built observability pipelines using OpenTelemetry, Grafana, and Prometheus; became the go-to resource on the team for observability tooling',
      'Titan Award Winner — top 5% of the organization',
    ],
    technologies: ['Java', 'Spring Boot', 'Kafka', 'MongoDB', 'OpenTelemetry', 'Grafana', 'Prometheus'],
    icon: <FaLandmark />,
  },
];

/* ── Compact early roles — shown below the main timeline ── */
const earlierExperiences = [
  {
    id: 5,
    company: 'BeeHouse Foundation (Richedu)',
    role: 'Blockchain Developer Intern',
    period: 'Apr 2021 – Jul 2021',
    location: 'Vancouver, Canada',
    image: richedu,
    icon: <FaLink />,
  },
  {
    id: 6,
    company: 'Jankalyan Multipurpose Education Society',
    role: 'Web Developer Intern',
    period: 'Nov 2020 – Dec 2020',
    location: 'India',
    image: jms,
    icon: <FaGlobe />,
  },
];

export default function Experience() {
  const [hoveredId, setHoveredId] = useState(null);
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.2 });

  return (
    <section className="experience" id="experience" ref={ref}>
      <style>{css}</style>

      <div className="container">
        <motion.div
          className="section-header"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <span className="section-tag">
            &lt; experience /&gt;
          </span>
          <h2 className="section-title">Professional Journey.</h2>
          <p className="section-description">
            Building impactful solutions across industry and academia
          </p>
        </motion.div>

        <div className="timeline">
          <div className="timeline-line" />

          {experiences.map((exp, i) => {
            const isLeft = i % 2 === 0;

            return (
              <motion.div
                key={exp.id}
                className={`timeline-item ${isLeft ? 'left' : 'right'}`}
                initial={{ opacity: 0, y: 40 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ delay: i * 0.15 }}
                onMouseEnter={() => setHoveredId(exp.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                <div className="timeline-dot">{exp.icon}</div>

                <div className="timeline-card">
                  <div className="card-header">
                    <div>
                      <h3>{exp.role}</h3>
                      <span className="company">{exp.company}</span>
                    </div>
                    <span className="period">{exp.period}</span>
                  </div>

                  <p className="description">{exp.description}</p>

                  <ul className="achievements">
                    {exp.achievements.map((a, idx) => (
                      <li key={idx}><span className="keyword">$</span> {a}</li>
                    ))}
                  </ul>

                  <div className="tech-stack">
                    {exp.technologies.map(t => (
                      <span key={t} className="tech">{t}</span>
                    ))}
                  </div>
                </div>

                <AnimatePresence>
                  {hoveredId === exp.id && (
                    <motion.div
                      className={`card-hover-image ${isLeft ? 'image-right' : 'image-left'}`}
                      initial={{ opacity: 0, scale: 0.92, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.2 }}
                    >
                      <img src={exp.image} alt={exp.company} />
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>

        {/* ── Earlier Experience — compact list ── */}
        <motion.div
          className="earlier-exp"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.7 }}
        >
          <div className="earlier-label">Earlier Experience</div>
          <div className="earlier-list">
            {earlierExperiences.map(exp => (
              <div key={exp.id} className="earlier-item">
                <span className="earlier-icon">{exp.icon}</span>
                <span className="earlier-role">{exp.role}</span>
                <span className="earlier-sep">·</span>
                <span className="earlier-company">{exp.company}</span>
                <span className="earlier-sep">·</span>
                <span className="earlier-period">{exp.period}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

const css = `
/* ---------- Layout ---------- */
ul {
  list-style-type: none;
}
.experience {
  padding: 8rem 0;
}

.container {
  max-width: 1200px;
  margin: auto;
  padding: 0 2rem;
}

/* ---------- Header ---------- */
.section-header {
  text-align: center;
  margin-bottom: 5rem;
}

.section-title {
  font-size: 2.4rem;
  font-weight: 700;
}

.section-description {
  color: #aaa;
  margin-top: .5rem;
}

.section-tag {
  color: #00ff41;
  font-family: monospace;
}

/* ---------- Timeline ---------- */
.timeline {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 3rem;
}

.timeline-line {
  position: absolute;
  left: 50%;
  top: 0;
  bottom: 0;
  width: 2px;
  transform: translateX(-50%);
  background: rgba(0,255,65,0.15);
  box-shadow: 0 0 10px rgba(0,255,65,0.08);
}

.timeline-item {
  position: relative;
  display: flex;
  width: 100%;
  align-items: flex-start;
}

.timeline-item.left {
  justify-content: flex-start;
}

.timeline-item.right {
  justify-content: flex-end;
}

/* Dot — top-aligned with card header, not vertically centred */
.timeline-dot {
  position: absolute;
  left: 50%;
  top: 1.75rem;
  transform: translateX(-50%);
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #0a0a0a;
  border: 2px solid #00ff41;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00ff41;
  font-size: 1.15rem;
  box-shadow: 0 0 18px rgba(0,255,65,0.35);
  z-index: 3;
}

/* Expanding pulse ring on each dot */
.timeline-dot::before {
  content: '';
  position: absolute;
  inset: -6px;
  border-radius: 50%;
  border: 1px solid rgba(0,255,65,0.4);
  animation: dot-ring 2.8s ease-out infinite;
}

@keyframes dot-ring {
  0%   { opacity: 0.7; transform: scale(1); }
  100% { opacity: 0;   transform: scale(1.8); }
}

/* Card — position:relative needed for the ::after connector arrow */
.timeline-card {
  position: relative;
  width: 44%;
  background: rgba(12,12,12,0.9);
  border: 1px solid rgba(0,255,65,0.18);
  border-radius: 16px;
  padding: 2rem;
  backdrop-filter: blur(12px);
  transition: transform .25s ease, box-shadow .25s ease, border-color .25s ease;
  z-index: 2;
}

.timeline-item:hover .timeline-card {
  transform: translateY(-5px);
  border-color: rgba(0,255,65,0.4);
  box-shadow: 0 16px 48px rgba(0,0,0,.6), 0 0 24px rgba(0,255,65,0.06);
}

/* Connector arrows — points toward the centre line / dot */
.timeline-item.left .timeline-card::after {
  content: '';
  position: absolute;
  top: calc(1.75rem + 16px);   /* vertically matches dot centre */
  right: -10px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-left: 10px solid rgba(0,255,65,0.2);
}

.timeline-item.right .timeline-card::after {
  content: '';
  position: absolute;
  top: calc(1.75rem + 16px);
  left: -10px;
  width: 0;
  height: 0;
  border-top: 10px solid transparent;
  border-bottom: 10px solid transparent;
  border-right: 10px solid rgba(0,255,65,0.2);
}

/* ---------- Card content ---------- */
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.card-header h3 {
  font-size: 1.05rem;
  font-weight: 700;
  line-height: 1.3;
  margin-bottom: 0.2rem;
}

.company {
  display: block;
  color: #00ff41;
  font-weight: 600;
  font-size: 0.9rem;
}

.period {
  font-size: .8rem;
  color: #777;
  font-family: 'Courier New', monospace;
  white-space: nowrap;
  padding-top: 0.15rem;
}

.description {
  margin: 1rem 0 0.75rem;
  color: #999;
  font-size: 0.875rem;
  line-height: 1.6;
}

/* Achievement list — spaced, flex rows so $ stays pinned */
.achievements {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.achievements li {
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;
  color: #c8c8c8;
  font-size: 0.855rem;
  line-height: 1.6;
}

.achievements .keyword {
  flex-shrink: 0;
  color: #00ff41;
  margin-top: 0.05rem;
}

.tech-stack {
  margin-top: 1.25rem;
  display: flex;
  flex-wrap: wrap;
  gap: .6rem;
}

.tech {
  font-size: .72rem;
  padding: .35rem .65rem;
  border-radius: 999px;
  border: 1px solid rgba(0,255,65,0.25);
  color: rgba(0,255,65,0.8);
  font-family: 'Courier New', monospace;
  transition: border-color .2s, color .2s;
}

.timeline-item:hover .tech {
  border-color: rgba(0,255,65,0.5);
  color: #00ff41;
}

/* ---------- Hover Image ---------- */
.card-hover-image {
  position: absolute;
  top: 1.75rem;       /* aligns with dot and card header, never bleeds into next entry */
  width: 280px;
  padding: 0.875rem;
  background: #0b0b0b;
  border-radius: 14px;
  box-shadow: 0 24px 64px rgba(0,0,0,.8);
  border: 1px solid rgba(0,255,65,0.2);
  z-index: 50;   /* above cards (z-index 2) and dots (z-index 3) */
}

.card-hover-image img {
  width: 100%;
  border-radius: 10px;
  display: block;
}

.timeline-item.left .image-right {
  left: 52%;
  margin-left: 40px;
}

.timeline-item.right .image-left {
  right: 52%;
  margin-right: 40px;
}

/* ---------- Earlier Experience ---------- */
.earlier-exp {
  margin-top: 3rem;
  padding: 1.25rem 1.75rem;
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 12px;
  background: rgba(255,255,255,0.015);
}

.earlier-label {
  font-family: 'Courier New', monospace;
  font-size: 0.68rem;
  color: rgba(0,255,65,0.45);
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 0.9rem;
}

.earlier-list {
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
}

.earlier-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  font-size: 0.84rem;
  color: #888;
  flex-wrap: wrap;
}

.earlier-icon {
  color: rgba(0,255,65,0.35);
  font-size: 0.82rem;
}

.earlier-role {
  color: #bbb;
  font-weight: 500;
}

.earlier-company {
  color: #777;
}

.earlier-period {
  color: #4a4a4a;
  font-family: 'Courier New', monospace;
  font-size: 0.76rem;
}

.earlier-sep {
  color: #2e2e2e;
}

/* ---------- Mobile ---------- */
@media (max-width: 900px) {
  .timeline-line {
    left: 24px;
  }

  .timeline-item {
    justify-content: flex-start !important;
  }

  .timeline-dot {
    left: 24px;
    top: 1.5rem;
    transform: none;
  }

  .timeline-card {
    width: calc(100% - 76px);
    margin-left: 76px;
  }

  /* hide connector arrow on mobile — card is flush against the rail */
  .timeline-item.left .timeline-card::after,
  .timeline-item.right .timeline-card::after {
    display: none;
  }

  .card-hover-image {
    display: none;
  }

  .earlier-item {
    gap: 0.4rem;
    font-size: 0.78rem;
  }
}
`;
